import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Form } from '@kuzmycz/react-form-ula';
import { Button, Modal } from '../../../../Components/uielements';
import BioPlusTextField from '../../../../Components/form-fields/BioPlusTextField';
import DropDown from '../../../../Components/uielements/dropDown';
import {
  ModalButtonWrapper,
  ModalGap,
  OutlineButton,
} from '../../../../services/settings/appStyle/global.style';
import * as CompanyService from '../../../../services/bioplus-services/company';
import { getstateList } from '../helper';
import { IntlMessages } from '../../../../helper/langHelper';
import {
  TwoColumns,
  ModalWrapper,
  SectionTitle,
  ProfileUploadContainer,
  ProfileUploadWrapper,
  ProfileImage,
  HiddenFileInput,
  UploadText,
  SubmitButton,
} from '../company.style';
import MultiSelectDropdown from '../../../../Components/uielements/multiSelectDropdown';
import { transformPointsOfInterestData } from '../ownerCompany';
import { formatDateForPayload, getCountry, parseDateFromPayload } from '../helper/helper';
import { useUserContext } from '../../../../hooks/UserContext';
import { getCountryCode } from '../../UserInvitation/userInvitation';

const DefaultUserIcon = () => (
  <svg width="60" height="60" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="60" cy="60" r="60" fill="#E0E0E0" />
    <circle cx="60" cy="45" r="20" fill="#999999" />
    <path
      d="M60 75C45 75 30 82 25 95C30 105 44 110 60 110C76 110 90 105 95 95C90 82 75 75 60 75Z"
      fill="#999999"
    />
  </svg>
);
const env = process.env.REACT_APP_ENV;
const EditCompanyModal = ({ onClose, data, companyId, getInterestList, matchPointsOfInterest }) => {
  const [selectedRole, setSelectedRole] = useState();
  const [selectedPOIs, setSelectedPOIs] = useState(
    transformPointsOfInterestData(matchPointsOfInterest) || []
  );
  const [selectedCountry, setSelectedCountrey] = useState();
  const [profileImageData, setProfileImageData] = useState({
    base64String: '',
    contentType: '',
  });
  const userContext = useUserContext();

  const countryPhoneCode = getCountryCode(process.env.REACT_APP_ENV);

  const profileImg = userContext?.user?.profileImg;
  const queryClient = useQueryClient();
  const [profileImage, setProfileImage] = useState(profileImg ? profileImg : null);

  const mutation = useMutation((values) => CompanyService.updateCompany(companyId, values), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('subscription');
      await queryClient.invalidateQueries('self');
      onClose();
    },
  });

  // Function to convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Function to get content type based on file extension
  const getContentType = (file) => {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    switch (fileExtension) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'webp':
        return 'image/webp';
      default:
        return 'image/jpeg'; // Default fallback
    }
  };

  // Handle profile image upload
  const handleProfileImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check if file type is supported
      const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!supportedTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, JPG, PNG, or WebP)');
        return;
      }

      try {
        const base64String = await convertToBase64(file);
        const contentType = getContentType(file);

        setProfileImage(URL.createObjectURL(file));
        setProfileImageData({
          base64String,
          contentType,
        });
      } catch (error) {
        console.error('Error converting image to base64:', error);
        alert('Error uploading image. Please try again.');
      }
    }
  };
  const handleSubmit = (values) => {
    const value = {
      companyName: values?.userData?.companyName,
      abn: values?.userData?.abn,
      firstName: values?.userData?.firstName,
      lastName: values?.userData?.lastName,
      preferedName: values?.userData?.preferedName,
      addressStreet: values?.userData.addressStreet,
      addressTown: values?.userData?.addressTown,
      ...(selectedRole
        ? { addressState: selectedRole }
        : { addressState: values?.userData?.addressState }),
      ...(selectedCountry
        ? { addressCountry: selectedCountry }
        : { addressCountry: values?.userData?.addressCountry }),
      zipCode: values?.userData?.zipCode,
      dateOfBirth: formatDateForPayload(values?.userData?.dateOfBirth, env),
      nationality: values?.userData?.nationality,
      driversLicence: values?.userData?.driversLicence,
      licenceType: values?.userData?.licenceType,
      renewalDateDrivers: formatDateForPayload(values?.userData?.renewalDateDrivers, env),
      passportDetails: values?.userData?.passportDetails,
      renewalDatePassport: formatDateForPayload(values?.userData?.renewalDatePassport, env),
      healthProvider: values?.userData?.healthProvider,
      contactNumber: values?.userData?.contactNumber,
      bloodType: values?.userData?.bloodType,
      nextOfKin: values?.userData?.nextOfKin,
      nextOfKinContact: values?.userData?.nextOfKinContact,
      interestList: selectedPOIs.map((poi) => poi.value),
      ...(profileImageData?.base64String && {
        base64String: profileImageData.base64String,
        contentType: profileImageData.contentType,
      }),
    };
    mutation.mutate(value);
  };

  const stateListBasedOnEnv = getstateList(process.env.REACT_APP_ENV);
  const country = getCountry(process.env.REACT_APP_ENV);

  const validator = (values) => {
    return undefined;
  };

  const handleSelectedValue = (value) => {
    setSelectedRole(value.value);
  };

  const handleSelectedCountryValue = (value) => {
    setSelectedCountrey(value.value);
  };

  const formatedData = {
    ...data,
    userData: {
      ...data.userData,
      phone: data?.userData?.phone || countryPhoneCode,
      nextOfKinContact: data?.userData?.nextOfKinContact || countryPhoneCode,
      dateOfBirth: parseDateFromPayload(data?.userData?.dateOfBirth, env),
      companyName: data?.companyData?.name,
      abn: data?.companyData?.abn,

      renewalDateDrivers: parseDateFromPayload(data?.userData?.renewalDateDrivers),

      renewalDatePassport: parseDateFromPayload(data?.userData?.renewalDatePassport, env),
    },
  };

  return (
    <>
      <Modal isOpen={true} onClose={onClose} title={<IntlMessages id="text.update.profile" />}>
        <Form initialValues={formatedData} validator={validator} onSubmit={handleSubmit}>
          <ModalWrapper>
            <ModalGap>
              {/* Profile Upload Section */}
              <ProfileUploadContainer>
                <SectionTitle>Upload Profile</SectionTitle>
                <ProfileUploadWrapper className={profileImage ? 'has-image' : ''}>
                  {profileImage ? (
                    <ProfileImage src={profileImage} alt="Profile" />
                  ) : (
                    <DefaultUserIcon />
                  )}
                  <HiddenFileInput
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp"
                    onChange={handleProfileImageUpload}
                  />
                </ProfileUploadWrapper>
                <UploadText>Click to upload profile picture</UploadText>
              </ProfileUploadContainer>
              <>
                <SectionTitle>Entity Information</SectionTitle>
                <TwoColumns style={{ gap: '10px' }}>
                  <IntlMessages id="label.first.name">
                    {(label) => (
                      <BioPlusTextField
                        label={label || 'First Name'}
                        name="userData.firstName"
                        fullWidth
                      />
                    )}
                  </IntlMessages>

                  <IntlMessages id="label.last.name">
                    {(label) => (
                      <BioPlusTextField
                        label={label || 'Last Name'}
                        name="userData.lastName"
                        fullWidth
                      />
                    )}
                  </IntlMessages>
                </TwoColumns>
                <TwoColumns style={{ gap: '10px' }}>
                  <IntlMessages id="input.label.company.name">
                    {(label) => (
                      <BioPlusTextField
                        label={label || 'Entity Name'}
                        name="userData.companyName"
                        fullWidth
                      />
                    )}
                  </IntlMessages>

                  <IntlMessages id="label.ABN">
                    {(label) => (
                      <BioPlusTextField
                        label={label || 'ABN'}
                        name="userData.abn"
                        fullWidth
                        maxlength="11"
                      />
                    )}
                  </IntlMessages>
                </TwoColumns>
                <TwoColumns style={{ gap: '10px' }}>
                  <IntlMessages id="label.preferred.name">
                    {(label) => (
                      <BioPlusTextField
                        label={label || 'Preferred Name'}
                        name="userData.preferedName"
                        fullWidth
                      />
                    )}
                  </IntlMessages>

                  <IntlMessages id="label.street">
                    {(label) => (
                      <BioPlusTextField label={label} name="userData.addressStreet" fullWidth />
                    )}
                  </IntlMessages>
                </TwoColumns>
                <TwoColumns style={{ gap: '10px' }}>
                  <IntlMessages id="label.email">
                    {(label) => (
                      <BioPlusTextField label={label} name="userData.email" fullWidth disabled />
                    )}
                  </IntlMessages>
                  <IntlMessages id="input.label.phone.no">
                    {(label) => (
                      <BioPlusTextField label={label} name="userData.phone" fullWidth disabled />
                    )}
                  </IntlMessages>
                </TwoColumns>{' '}
                <TwoColumns style={{ gap: '10px' }}>
                  <IntlMessages id="label.country">
                    {(label) => (
                      <DropDown
                        options={country}
                        selectedOption={handleSelectedCountryValue}
                        name="country"
                        defaultValue={data?.userData?.addressCountry}
                        placeholder={label}
                        label="Country"
                      />
                    )}
                  </IntlMessages>

                  <IntlMessages id="label.state">
                    {(label) => (
                      <DropDown
                        options={stateListBasedOnEnv}
                        selectedOption={handleSelectedValue}
                        name="state"
                        defaultValue={data?.userData?.addressState}
                        placeholder={label}
                        label="State"
                      />
                    )}
                  </IntlMessages>
                </TwoColumns>
                <TwoColumns style={{ gap: '10px' }}>
                  <IntlMessages id="label.town">
                    {(label) => (
                      <BioPlusTextField label={label} name="userData.addressTown" fullWidth />
                    )}
                  </IntlMessages>

                  <IntlMessages id="label.zipcode">
                    {(label) => (
                      <BioPlusTextField label={label} name="userData.zipCode" fullWidth />
                    )}
                  </IntlMessages>
                </TwoColumns>
              </>
              {/* points of interest */}
              <MultiSelectDropdown
                options={
                  getInterestList?.isLoading
                    ? []
                    : transformPointsOfInterestData(getInterestList?.data)
                }
                selectedItems={selectedPOIs}
                onSelectionChange={setSelectedPOIs}
                label="Select Points of Interest"
                placeholder="Choose your interests..."
                isKeepDropdownOpen={true}
              />
              <>
                <SectionTitle>Personal Details</SectionTitle>
                <TwoColumns style={{ gap: '10px' }}>
                  <IntlMessages id="label.date.of.birth">
                    {(label) => (
                      <BioPlusTextField
                        label={label}
                        name="userData.dateOfBirth"
                        fullWidth
                        type="date"
                      />
                    )}
                  </IntlMessages>

                  <IntlMessages id="label.nationality">
                    {(label) => (
                      <BioPlusTextField label={label} name="userData.nationality" fullWidth />
                    )}
                  </IntlMessages>
                </TwoColumns>
              </>
              <>
                <SectionTitle>Identification Details ('Driving License')</SectionTitle>
                <TwoColumns style={{ gap: '10px' }}>
                  <IntlMessages id="label.drivers.licence">
                    {(label) => (
                      <BioPlusTextField label={label} name="userData.driversLicence" fullWidth />
                    )}
                  </IntlMessages>
                  <IntlMessages id="label.licence.type">
                    {(label) => (
                      <BioPlusTextField label={label} name="userData.licenceType" fullWidth />
                    )}
                  </IntlMessages>
                </TwoColumns>
                <TwoColumns>
                  <IntlMessages id="label.renewal.date">
                    {(label) => (
                      <BioPlusTextField
                        label={label}
                        name="userData.renewalDateDrivers"
                        fullWidth
                        type="date"
                      />
                    )}
                  </IntlMessages>
                </TwoColumns>
              </>
              <>
                <SectionTitle>Identification Details ('Passport')</SectionTitle>
                <TwoColumns style={{ gap: '10px' }}>
                  <IntlMessages id="label.passport.details">
                    {(label) => (
                      <BioPlusTextField label={label} name="userData.passportDetails" fullWidth />
                    )}
                  </IntlMessages>
                  <IntlMessages id="label.passport.renewal.date">
                    {(label) => (
                      <BioPlusTextField
                        label={label}
                        name="userData.renewalDatePassport"
                        fullWidth
                        type="date"
                      />
                    )}
                  </IntlMessages>
                </TwoColumns>
              </>
              <SectionTitle>Emergency Information</SectionTitle>
              <TwoColumns style={{ gap: '10px' }}>
                <IntlMessages id="label.contact.number">
                  {(label) => (
                    <BioPlusTextField label={label} name="userData.contactNumber" fullWidth />
                  )}
                </IntlMessages>

                <IntlMessages id="label.blood.type">
                  {(label) => (
                    <BioPlusTextField label={label} name="userData.bloodType" fullWidth />
                  )}
                </IntlMessages>
              </TwoColumns>
              <TwoColumns style={{ gap: '10px' }}>
                <IntlMessages id="label.next.of.kin">
                  {(label) => (
                    <BioPlusTextField label={label} name="userData.nextOfKin" fullWidth />
                  )}
                </IntlMessages>

                <IntlMessages id="label.next.of.kin.contact">
                  {(label) => (
                    <BioPlusTextField label={label} name="userData.nextOfKinContact" fullWidth />
                  )}
                </IntlMessages>
              </TwoColumns>{' '}
              <TwoColumns>
                <IntlMessages id="label.health.provider">
                  {(label) => (
                    <BioPlusTextField label={label} name="userData.healthProvider" fullWidth />
                  )}
                </IntlMessages>
              </TwoColumns>
            </ModalGap>
            <ModalButtonWrapper style={{ paddingBottom: '20px' }}>
              <OutlineButton onClick={onClose}>
                <IntlMessages id="modal.button.cancel" />
              </OutlineButton>
              <SubmitButton varient="normal" type="submit">
                {mutation.isLoading && <span class="loader" style={{ marginRight: '4px' }}></span>}
                <IntlMessages id="modal.button.submit" />
              </SubmitButton>
            </ModalButtonWrapper>
          </ModalWrapper>
        </Form>
      </Modal>
    </>
  );
};

export default EditCompanyModal;
